
// Import necessary components and hooks
import { useState, useEffect } from 'react';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { PrimarySchedule } from './payroll/PrimarySchedule';
import { AdditionalSchedule } from './payroll/AdditionalSchedule';
import type { Application, PayScheduleType, PayrollSchedule as PayrollScheduleType } from '@/types/database.types';

interface PayrollScheduleProps {
  data: Partial<Application>;
  onSave: (data: Partial<Application>) => Promise<void>;
}

export const PayrollSchedule = ({ data, onSave }: PayrollScheduleProps) => {
  const { toast } = useToast();
  const [hasMultipleSchedules, setHasMultipleSchedules] = useState(
    Array.isArray(data.additional_schedules) && data.additional_schedules.length > 0
  );

  // Initialize the form with data from props
  const { control, register, handleSubmit, reset, watch, setValue } = useForm<Partial<Application>>({
    defaultValues: {
      pay_schedule_type: data.pay_schedule_type || 'Weekly',
      pay_period_start: data.pay_period_start || undefined,
      pay_period_end: data.pay_period_end || undefined,
      next_check_date: data.next_check_date || undefined,
      additional_schedules: data.additional_schedules || [],
      multiple_schedules_desc: data.multiple_schedules_desc || '',
      // Add bonus and commission fields
      has_bonuses: data.has_bonuses || false,
      bonus_structure_desc: data.bonus_structure_desc || '',
      has_commission: data.has_commission || false,
      commission_structure_desc: data.commission_structure_desc || '',
    }
  });

  // Set up field array for additional schedules
  const { fields, append, remove } = useFieldArray({
    control,
    name: "additional_schedules",
  });

  // Reset form when data changes
  useEffect(() => {
    console.log("PayrollSchedule - Resetting form with data:", data);
    reset({
      pay_schedule_type: data.pay_schedule_type || 'Weekly',
      pay_period_start: data.pay_period_start || undefined,
      pay_period_end: data.pay_period_end || undefined,
      next_check_date: data.next_check_date || undefined,
      additional_schedules: data.additional_schedules || [],
      multiple_schedules_desc: data.multiple_schedules_desc || '',
      // Add bonus and commission fields
      has_bonuses: data.has_bonuses || false,
      bonus_structure_desc: data.bonus_structure_desc || '',
      has_commission: data.has_commission || false,
      commission_structure_desc: data.commission_structure_desc || '',
    });

    setHasMultipleSchedules(
      Array.isArray(data.additional_schedules) && data.additional_schedules.length > 0
    );
  }, [data, reset]);

  // Add a new schedule to the field array
  const addSchedule = () => {
    append({
      schedule_type: 'Weekly',
      next_check_date: '',
      pay_period_start: '',
      pay_period_end: '',
      description: ''
    });
  };

  // Form submission handler
  const onSubmit = handleSubmit(async (formData) => {
    try {
      console.log("PayrollSchedule - Saving form data:", formData);
      // If we're not using multiple schedules, clear the additional schedules array
      const finalData = {
        ...formData,
        additional_schedules: hasMultipleSchedules ? formData.additional_schedules : []
      };

      await onSave(finalData);
      toast({
        title: "Payroll schedule saved",
        description: "The payroll schedule information has been updated.",
      });
    } catch (error) {
      console.error("Error saving payroll schedule:", error);
      toast({
        title: "Error saving data",
        description: "There was an error saving the payroll schedule information.",
        variant: "destructive",
      });
    }
  });

  // Setup form change handler to autosave
  const handleFormChange = (e: React.FormEvent) => {
    e.preventDefault(); // Prevent form submission
    console.log("PayrollSchedule - Form changed, triggering submit");
    onSubmit(); // Call the submit handler when form changes
  };

  // Watch for bonus and commission values
  const hasBonus = watch('has_bonuses');
  const hasCommission = watch('has_commission');

  return (
    <form onChange={handleFormChange} onSubmit={(e) => e.preventDefault()}>
      <Card>
        <CardContent className="pt-6 space-y-6">
          <h2 className="text-2xl font-semibold">Payroll Schedule</h2>
          <p className="text-muted-foreground">
            Please provide information about the organization's payroll schedule.
          </p>

          <div className="space-y-6">
            <PrimarySchedule
              control={control}
              register={register}
              watch={watch}
              setValue={setValue}
            />

            <div className="space-y-4 border-t pt-4">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="hasMultiple"
                  checked={hasMultipleSchedules}
                  onChange={(e) => setHasMultipleSchedules(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <Label htmlFor="hasMultiple">There are multiple payroll schedules</Label>
              </div>

              {hasMultipleSchedules && (
                <>
                  <Label htmlFor="multiple_schedules_desc">Describe why there are multiple schedules</Label>
                  <Input
                    id="multiple_schedules_desc"
                    {...register('multiple_schedules_desc')}
                    placeholder="e.g., Different departments have different pay schedules"
                  />

                  <div className="mt-4 space-y-6">
                    {fields.map((field, index) => (
                      <AdditionalSchedule
                        key={field.id}
                        index={index}
                        control={control}
                        remove={remove}
                      />
                    ))}

                    <Button
                      type="button"
                      variant="outline"
                      onClick={addSchedule}
                      className="mt-2"
                    >
                      Add Another Schedule
                    </Button>
                  </div>
                </>
              )}
            </div>

            {/* Bonus structure section */}
            <div className="space-y-4 border-t pt-4">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="hasBonus"
                  checked={!!hasBonus}
                  {...register('has_bonuses')}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <Label htmlFor="hasBonus">The organization offers bonuses</Label>
              </div>

              {hasBonus && (
                <div className="ml-6">
                  <Label htmlFor="bonus_structure_desc">Describe the bonus structure</Label>
                  <Input
                    id="bonus_structure_desc"
                    {...register('bonus_structure_desc')}
                    placeholder="e.g., Annual performance bonus based on goals"
                  />
                </div>
              )}
            </div>

            {/* Commission structure section */}
            <div className="space-y-4 border-t pt-4">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="hasCommission"
                  checked={!!hasCommission}
                  {...register('has_commission')}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <Label htmlFor="hasCommission">The organization offers commissions</Label>
              </div>

              {hasCommission && (
                <div className="ml-6">
                  <Label htmlFor="commission_structure_desc">Describe the commission structure</Label>
                  <Input
                    id="commission_structure_desc"
                    {...register('commission_structure_desc')}
                    placeholder="e.g., Sales team receives 5% commission on deals"
                  />
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end">
            <Button type="button" onClick={() => onSubmit()}>Save Schedule</Button>
          </div>
        </CardContent>
      </Card>
    </form>
  );
};
